import React, { useEffect, useState } from 'react';
import { ITaskType } from '../Interfaces/ITaskType';
import { ITaskPriority } from '../Interfaces/ITaskPriority';
import { IUser } from '../Interfaces/IUser';
import { IUserGroup } from '../Interfaces/IUserGroup';
import {
  useGetTaskFullObject,
  useWriteTaskFullObject,
} from '../hooks/reactQuerys/TaskQueries';
import { taskFullObjectSchema } from '../Interfaces/ITask';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { EditTask } from './EditTask';
import { useAlert } from '../context/AlertContext';

interface IProps {
  idTask: number;
  setIdTask: React.Dispatch<React.SetStateAction<number | null>>;
  taskTypes: ITaskType[];
  taskPriority: ITaskPriority[];
  refatchTasks: Function;
  users: IUser[];
  userGroups: IUserGroup[];
}

export const DialogTask: React.FC<IProps> = ({
  idTask,
  setIdTask,
  taskTypes,
  taskPriority,
  refatchTasks,
  users,
  userGroups,
}) => {
  const { showAlert } = useAlert();
  const { writeTaskFullObject, isPendingWriteTaskFullObject } =
    useWriteTaskFullObject();
  const { taskFullObject, isLoadingTaskFullObject, refetchTaskFullObject } =
    useGetTaskFullObject(idTask === -1 ? undefined : idTask);
  const [editTask, setEditTask] = useState(taskFullObjectSchema.parse({}));

  useEffect(() => {
    if (taskFullObject) {
      setEditTask(taskFullObject);
    }
  }, [taskFullObject]);

  // #1250: Erledigte Aufgabe bleibt einsehbar (read-only); über den
  // „Wieder öffnen"-Button wird sie lokal geöffnet und editierbar,
  // gespeichert wird erst mit „Speichern".
  const wasClosed =
    taskFullObject !== undefined && taskFullObject.Closed_at !== null;
  const isReopened = wasClosed && editTask.Closed_at === null;

  const handleClose = () => {
    setIdTask(null);
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const uploadObject = taskFullObjectSchema.parse(editTask);
      await writeTaskFullObject(uploadObject);
      refatchTasks();
      showAlert({
        severity: 'success',
        text: 'Aufgabe erfolgreich gespeichert!',
      });
      handleClose();
    } catch (error) {
      console.error(error);
      showAlert({
        severity: 'error',
        text: 'Fehler beim Speichern der Aufgabe!',
      });
    }
  };

  if (isLoadingTaskFullObject) {
    return (
      <Dialog open>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }
  if (idTask !== -1 && !taskFullObject) {
    return (
      <Dialog open onClose={handleClose}>
        <DialogContent>
          <Alert severity='error'>Es ist ein Fehler aufgetretten!</Alert>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={handleClose}>
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={handleClose} maxWidth='md' fullWidth>
      <form onSubmit={handleSave}>
        <DialogContent>
          <EditTask
            key={`edit-task-${editTask.idTask}`}
            editTask={editTask}
            setEditTask={setEditTask}
            taskPriority={taskPriority}
            taskTypes={taskTypes}
            users={users}
            userGroups={userGroups}
            disableMarkDone={taskFullObject === undefined}
            disableEdit={wasClosed && editTask.Closed_at !== null}
          />
        </DialogContent>
        <DialogActions>
          {editTask.idTask < 0 || !wasClosed || isReopened ? (
            <>
              <Button
                variant='outlined'
                onClick={handleClose}
                disabled={isPendingWriteTaskFullObject}
              >
                Abbruch
              </Button>
              <Button
                variant='contained'
                type='submit'
                disabled={isPendingWriteTaskFullObject}
              >
                {isPendingWriteTaskFullObject ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    <span>Speichern</span>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  'Speichern '
                )}
              </Button>
            </>
          ) : (
            <Button variant='contained' onClick={handleClose}>
              Okay
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};
